'use client';

import { useEffect, useRef } from 'react';
import styles from './CartAuthStepClient.module.css';
import {
  type AuthViewModeType,
  type LoginState,
  type RegisterState,
  type ForgotPasswordState,
  AuthFlow,
  AUTH_VIEW_MODE
} from '@pixum/auth-ui';
import { useCartAuthHandlers, useCartAuthNavigation, useCustomerScore } from '../../../hooks/auth';
import { SnackBar } from '@pixum/snackbar';
import { Icon } from '@pixum/icon';
import Text, { TEXT_TAG, TEXT_VARIANT } from '@pixum/text';
import { CartPayPalExpressHint } from '../../partials/CartPayPalExpressHint';
import { Placeholder } from 'components';
import {
  trackForgotPasswordClick,
  trackSalutationChange,
  trackNewsletterChange,
  trackLoginTabClick,
  trackRegisterTabClick
} from '../../../analytics/auth';
import { ForgotPasswordSuccess } from '../../partials/ForgotPasswordSuccess';

interface CartAuthStepClientProps {
  constants: Record<string, string>;
  initialView: AuthViewModeType;
  locale: string;
  isAuthenticated: boolean;
  loginAction: (prevState: LoginState, formData: FormData) => Promise<LoginState>;
  registerAction: (prevState: RegisterState, formData: FormData) => Promise<RegisterState>;
  forgotPasswordAction: (prevState: ForgotPasswordState, formData: FormData) => Promise<ForgotPasswordState>;
  getPostLoginRedirectAction: () => Promise<string>;
  overviewHref: string;
  loginHref: string;
  registerHref: string;
  forgotPasswordHref: string;
  checkoutHref: string;
  addressHref: string;
  isPayPalExpress: boolean;
  initialEmail?: string;
}

export function CartAuthStepClient({
  constants,
  initialView,
  locale,
  isAuthenticated,
  loginAction,
  registerAction,
  forgotPasswordAction,
  getPostLoginRedirectAction,
  overviewHref,
  loginHref,
  registerHref,
  forgotPasswordHref,
  checkoutHref,
  addressHref,
  isPayPalExpress,
  initialEmail
}: CartAuthStepClientProps) {
  const formRef = useRef<HTMLDivElement>(null);
  const {
    currentView,
    authFlowKey,
    loginError,
    isForgotPasswordSuccess,
    isNavigatingBack,
    setLoginError,
    setIsForgotPasswordSuccess,
    handleViewChange,
    handleBackToCart
  } = useCartAuthNavigation({ initialView, overviewHref, loginHref, registerHref, forgotPasswordHref });

  useCustomerScore({ locale, isAuthenticated, initialView, onViewRecommended: handleViewChange });

  // Restore focus to the form container when AuthFlow remounts due to back/forward navigation.
  // AuthFlow's key change unmounts/remounts the tree, so focus falls to <body>.
  // Skip authFlowKey === 0 (initial mount) — focus should not be stolen on page load.
  useEffect(() => {
    if (authFlowKey > 0) {
      formRef.current?.focus();
    }
  }, [authFlowKey]);

  const { handleLogin, handleRegister, handleForgotPassword } = useCartAuthHandlers({
    loginAction,
    registerAction,
    forgotPasswordAction,
    getPostLoginRedirectAction,
    isPayPalExpress,
    checkoutHref,
    addressHref,
    setLoginError,
    setIsForgotPasswordSuccess
  });

  if (isForgotPasswordSuccess) {
    return (
      <ForgotPasswordSuccess
        constants={constants}
        onBackToLogin={() => handleViewChange(AUTH_VIEW_MODE.LOGIN)}
      />
    );
  }

  const agbLabel = constants['i18n_company_policy'] ? (
    <Placeholder
      placeholderContent={constants['i18n_company_policy']}
      contentToReplaceWith={constants['i18n_company_policy_placeholder']}
      href={constants['i18n_company_policy_route']}
      openInNewTab
    />
  ) : undefined;

  const dsgvoLabel = constants['i18n_data_privacy'] ? (
    <Placeholder
      placeholderContent={constants['i18n_data_privacy']}
      contentToReplaceWith={constants['i18n_data_privacy_placeholder']}
      href={constants['i18n_company_privacy_route']}
      openInNewTab
    />
  ) : undefined;

  const isPayPalExpressLoginView = isPayPalExpress && currentView === AUTH_VIEW_MODE.LOGIN;
  const isForgotPasswordView = currentView === AUTH_VIEW_MODE.FORGOT_PASSWORD;
  const hasLiveContent = !!loginError || isPayPalExpressLoginView || isForgotPasswordView;

  return (
    // tabIndex={-1} makes the container programmatically focusable (not in tab order).
    // Focus is moved here by the useEffect above when AuthFlow remounts on back/forward navigation.
    <div ref={formRef} tabIndex={-1} className={styles.container}>
      <AuthFlow
        key={authFlowKey}
        loginAction={handleLogin}
        registerAction={handleRegister}
        forgotPasswordAction={handleForgotPassword}
        translations={{
          headline: constants['i18n_login_or_create_account'],
          forgotPasswordHeadline: constants['i18n_password_forgotten_title'],
          toggleLogin: constants['i18n_cart_auth_existing_customer'],
          toggleRegister: constants['i18n_cart_auth_new_customer'],
          login: {
            email: constants['i18n_my_pixum_email'],
            password: constants['i18n_password'],
            forgotPasswordLink: constants['i18n_password_forgotten'],
            loginButton: constants['i18n_anmelden'],
            backToOverview: constants['i18n_back-to-cart'],
            togglePasswordButton: constants['i18n_password_tooltip']
          },
          register: {
            salutation: constants['i18n_salutation'],
            salutationPlaceholder: constants['i18n_gender_placeholder'],
            salutationMale: constants['i18n_mr_abbreviation'],
            salutationFemale: constants['i18n_mrs_abbreviation'],
            salutationDiverse: constants['i18n_register_none_specified'],
            firstName: constants['i18n_first_name'],
            lastName: constants['i18n_last_name'],
            email: constants['i18n_my_pixum_email'],
            agbLabel: agbLabel,
            dsgvoLabel: dsgvoLabel,
            newsletter: constants['i18n_receive_personalised_newsletters'],
            registerButton: constants['i18n_register'],
            backToOverview: constants['i18n_back-to-cart']
          },
          forgotPassword: {
            email: constants['i18n_my_pixum_email'],
            submitButton: constants['i18n_send'],
            backToLogin: constants['i18n_back_to_login'],
            successMessage: constants['i18n_password_reset_link']
          }
        }}
        initialView={currentView}
        initialEmail={initialEmail}
        forgotPasswordHref={forgotPasswordHref}
        onBackToCart={handleBackToCart}
        isNavigationBackLoading={isNavigatingBack}
        onViewChange={handleViewChange}
        onSalutationChange={trackSalutationChange}
        onNewsletterChange={trackNewsletterChange}
        forgotPasswordTrackEvent={trackForgotPasswordClick}
        loginTrackEvent={trackLoginTabClick}
        registerTrackEvent={trackRegisterTabClick}
      >
        {hasLiveContent && (
          <div aria-live="assertive" aria-atomic="true">
            {loginError && (
              <SnackBar
                message={loginError}
                linkText={constants['i18n_close'] || '✕'}
                icon={<Icon name="warningsignal" ariaHidden />}
                theme={{ base: 'rose', accent: 'rose', brightness: 'medium' }}
                onClick={() => setLoginError(null)}
              />
            )}

            {isPayPalExpressLoginView && (
              <CartPayPalExpressHint
                title={constants['i18n_cart_paypal_express_login_hint_title']}
                text={constants['i18n_cart_paypal_express_login_hint_text']}
              />
            )}

            {isForgotPasswordView && (
              <Text tag={TEXT_TAG.P} variant={TEXT_VARIANT.BODY}>
                {constants['i18n_enter_email_for_password_recovery']}
              </Text>
            )}
          </div>
        )}
      </AuthFlow>
    </div>
  );
}
